import classNames from 'classnames'
import {
  ToggleCollectionContentAssociation,
  ToggleCollectionContentAssociationVariables,
  Visibility,
} from 'globalTypes'

import { useMutation } from '@apollo/client'
import ChangeVisibility from 'Root/App/Dialog/ChangeVisibility'
import Audio from 'Root/App/Item/Audio'
import Event from 'Root/App/Item/Event'
import ImageItem from 'Root/App/Item/Image'
import ItemMenu from 'Root/App/Item/ItemMenu'
import LinkItem from 'Root/App/Item/Link'
import Pdf from 'Root/App/Item/Pdf'
import Quiz from 'Root/App/Item/Quiz'
import TextItem from 'Root/App/Item/Text'
import Video from 'Root/App/Item/Video'
import mixpanelTrack from 'lib/mixpanel-track'

import MUTATION from '../../AssociationContext/AssociateContents/toggle-collection-content-association.graphql'
import MODULE_MUTATION from '../../AssociationContext/AssociateContents/toggle-collection-module-content-association.graphql'
import Link from './Link'
import UPDATE_VISIBILITY_FOR_COLLECTION_MUTATION from './update-visibility-for-collection.graphql'

interface Props {
  className?: string
  content?: any
  collectionSlug?: string
  collectionId?: string
  collectionVisibility?: string
  canReadCollection?: boolean
  canManageCollection?: boolean
  refetchOnUpdate?: Function
  isModule?: boolean
  moduleId?: string
}

const Item = ({
  className,
  content,
  collectionSlug,
  collectionId,
  collectionVisibility,
  canReadCollection,
  canManageCollection,
  refetchOnUpdate,
  isModule,
  moduleId,
}: Props) => {
  const newContent = {
    ...content,
    canReadCollection,
    canManageCollection,
    collectionSlug,
  }

  const itemLinkInlineStyle = canManageCollection
    ? {
        padding: '0.75rem 0.75rem 0rem 0.75rem',
        borderRadius: '0.75rem 0.75rem 0 0',
      }
    : undefined

  const [mutate] = useMutation<
    ToggleCollectionContentAssociation,
    ToggleCollectionContentAssociationVariables
  >(MUTATION)

  const [updateVisibilityForCollection] = useMutation(
    UPDATE_VISIBILITY_FOR_COLLECTION_MUTATION
  )

  const [removeContentFromModule] = useMutation(MODULE_MUTATION)

  const onRemove = () => async (event) => {
    event.preventDefault()

    mixpanelTrack('Rimozione Contenuto', {
      id_corso: collectionId,
      id_contenuto: content.id,
      tipo_contenuto: content.__typename,
    })

    if (isModule) {
      await removeContentFromModule({
        variables: {
          associated: false,
          collectionId,
          moduleId,
          contentId: content.id,
        },
      }).then(refetchOnUpdate())
    } else {
      await mutate({
        variables: {
          contentId: content.id,
          collectionId,
          associated: false,
        },
      }).then(refetchOnUpdate())
    }
  }

  const onUpdateVisibilityForCollection = async (visibility: Visibility) => {
    await updateVisibilityForCollection({
      variables: {
        contentId: content.id,
        collectionId: collectionId,
        visibility: visibility,
      },
    }).then(refetchOnUpdate())
  }

  return (
    <span>
      <Link
        className={classNames(
          className,
          !canManageCollection &&
            newContent?.isUnlocked === false &&
            'pointer-events-none opacity-50'
        )}
        content={newContent}
        collectionVisibility={collectionVisibility}
        customStyle={itemLinkInlineStyle}
      >
        {content.__typename === 'AudioContent' && (
          <Audio content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'EventContent' && (
          <Event content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'ImageContent' && (
          <ImageItem content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'LinkContent' && (
          <LinkItem content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'PdfContent' && (
          <Pdf content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'QuizContent' && (
          <Quiz content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'TextContent' && (
          <TextItem content={newContent} canManage={canManageCollection} />
        )}
        {content.__typename === 'VideoContent' && (
          <Video content={newContent} canManage={canManageCollection} />
        )}
      </Link>
      {newContent?.canManageCollection && (
        <div className="flex justify-end pr-3 pb-3 bg-gray-100 rounded-b-xl">
          {collectionVisibility === Visibility.public && (
            <ChangeVisibility
              id={content.id}
              collectionId={collectionId}
              visibility={content.visibilityForCollection}
              onUpdateVisibility={onUpdateVisibilityForCollection}
            />
          )}

          <ItemMenu
            key={content.title}
            onRemove={onRemove()}
            isModule={isModule}
            canManageCollection={newContent?.canManageCollection}
          />
        </div>
      )}
    </span>
  )
}

export default Item
